import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

// Diese Funktion müsste in deiner API implementiert werden
import { 
  DbProduct, 
  AdminUser, 
  fetchAdminStats, 
  fetchRecentProducts, 
  fetchRecentUsers, 
  fetchActiveUsers,
  fetchVisitorStats,
  deleteProduct, 
  banUser 
} from '../../utils/api';

// Neue Interfaces für die erweiterten Funktionen
interface ActiveUser {
  id: number;
  name: string;
  email: string;
  last_activity: string; // ISO String Format
}

interface DailyVisitor {
  date: string;
  visitors: number;
}

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    activeProducts: 0,
    totalMessages: 0,
    newUsersToday: 0,
    newProductsToday: 0
  });
  const [recentProducts, setRecentProducts] = useState<DbProduct[]>([]);
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Neue State-Variablen für die erweiterten Funktionen
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [visitorStats, setVisitorStats] = useState<DailyVisitor[]>([]);

  // Wenn du willst, kannst du ein Array von Admin-IDs oder einen speziellen Role-Check implementieren
  const isAdmin = user?.email === 'info@sportyreplay.de';

  useEffect(() => {
    // Redirecten, wenn kein Admin
    if (!isAdmin) {
      navigate('/');
      return;
    }
    
    const loadAdminData = async () => {
      setLoading(true);
      try {
        // Stats laden
        try {
          const statsData = await fetchAdminStats();
          setStats(statsData);
        } catch (err) {
          console.error('Fehler beim Laden der Statistiken:', err);
          // Setze leere Stats, aber fahre fort
          setStats({
            totalUsers: 0,
            totalProducts: 0,
            activeProducts: 0,
            totalMessages: 0,
            newUsersToday: 0,
            newProductsToday: 0
          });
        }
        
        // Produkte laden
        try {
          const productsData = await fetchRecentProducts();
          setRecentProducts(productsData);
        } catch (err) {
          console.error('Fehler beim Laden der Produkte:', err);
          setRecentProducts([]);
        }
        
        // Benutzer laden
        try {
          const usersData = await fetchRecentUsers();
          setRecentUsers(usersData);
        } catch (err) {
          console.error('Fehler beim Laden der Benutzer:', err);
          setRecentUsers([]);
        }

        // Aktive Benutzer laden (neu)
        try {
          const activeUsersData = await fetchActiveUsers();
          setActiveUsers(activeUsersData);
        } catch (err) {
          console.error('Fehler beim Laden der aktiven Benutzer:', err);
          setActiveUsers([]);
        }

        // Besucherstatistiken laden (neu)
        try {
          const visitorData = await fetchVisitorStats();
          setVisitorStats(visitorData);
        } catch (err) {
          console.error('Fehler beim Laden der Besucherstatistiken:', err);
          setVisitorStats([]);
        }
      } catch (err) {
        console.error('Allgemeiner Fehler beim Laden der Admin-Daten:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadAdminData();
  }, [isAdmin, navigate]);

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm('Möchtest du dieses Produkt wirklich löschen?')) {
      try {
        await deleteProduct(productId);
        setRecentProducts(recentProducts.filter(product => product.id !== productId));
        alert('Produkt erfolgreich gelöscht');
      } catch (err) {
        console.error('Fehler beim Löschen des Produkts:', err);
        alert('Fehler beim Löschen des Produkts');
      }
    }
  };

  const handleBanUser = async (userId: number) => {
    if (window.confirm('Möchtest du diesen Benutzer wirklich sperren?')) {
      try {
        await banUser(userId);
        setRecentUsers(recentUsers.map(user => 
          user.id === userId ? { ...user, status: 'banned' } : user
        ));
        alert('Benutzer erfolgreich gesperrt');
      } catch (err) {
        console.error('Fehler beim Sperren des Benutzers:', err);
        alert('Fehler beim Sperren des Benutzers');
      }
    }
  };

  // Hilfsfunktion, um sicher den Preis zu formatieren
  const formatPrice = (price: any): string => {
    if (price === null || price === undefined) return "0.00";
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return "0.00";
    return numPrice.toFixed(2);
  };

  // Hilfsfunktion zum Formatieren des Datums (mit Zeit)
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Hilfsfunktion, um die Letzte-Aktivität zu formatieren
  const formatLastActivity = (dateString: string): string => {
    const now = new Date();
    const activityDate = new Date(dateString);
    const diffMs = now.getTime() - activityDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `Vor ${diffMins} Minuten`;
    if (diffHours < 24) return `Vor ${diffHours} Stunden`;
    return `Vor ${diffDays} Tagen`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loader">Lädt Admin-Daten...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8">Admin Dashboard</h1>

        {/* Tabs - Mobile optimiert mit Scrollbar */}
        <div className="bg-white rounded-lg shadow-md mb-6 sm:mb-8 overflow-x-auto">
          <div className="flex border-b min-w-max">
            <button 
              className={`py-3 sm:py-4 px-3 sm:px-6 text-sm sm:text-base font-medium ${activeTab === 'dashboard' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-orange-500'}`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
            <button 
              className={`py-3 sm:py-4 px-3 sm:px-6 text-sm sm:text-base font-medium ${activeTab === 'products' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-orange-500'}`}
              onClick={() => setActiveTab('products')}
            >
              Produkte
            </button>
            <button 
              className={`py-3 sm:py-4 px-3 sm:px-6 text-sm sm:text-base font-medium ${activeTab === 'users' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-orange-500'}`}
              onClick={() => setActiveTab('users')}
            >
              Benutzer
            </button>
            <button 
              className={`py-3 sm:py-4 px-3 sm:px-6 text-sm sm:text-base font-medium ${activeTab === 'settings' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-orange-500'}`}
              onClick={() => setActiveTab('settings')}
            >
              Einstellungen
            </button>
          </div>
        </div>

        {/* Dashboard Content */}
        {activeTab === 'dashboard' && (
          <>
            {/* Statistik-Karten - Mobile optimiert mit Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-gray-500 text-xs sm:text-sm uppercase mb-2">Gesamtnutzer</h2>
                <div className="flex items-center">
                  <div className="rounded-full bg-blue-100 p-2 sm:p-3 mr-3 sm:mr-4">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.totalUsers}</p>
                    <p className="text-green-500 text-xs sm:text-sm">+{stats.newUsersToday} heute</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-gray-500 text-xs sm:text-sm uppercase mb-2">Gesamtprodukte</h2>
                <div className="flex items-center">
                  <div className="rounded-full bg-orange-100 p-2 sm:p-3 mr-3 sm:mr-4">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.totalProducts}</p>
                    <p className="text-green-500 text-xs sm:text-sm">+{stats.newProductsToday} heute</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-gray-500 text-xs sm:text-sm uppercase mb-2">Nachrichten</h2>
                <div className="flex items-center">
                  <div className="rounded-full bg-purple-100 p-2 sm:p-3 mr-3 sm:mr-4">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.totalMessages}</p>
                    <p className="text-xs sm:text-sm">Gesamt</p>
                  </div>
                </div>
              </div>
            </div>

            {/* NEU: Besucherstatistik - Mobile optimiert mit responsiver Tabelle */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Besucherstatistik</h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Besucher</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visitorStats.map(stat => (
                        <tr key={stat.date}>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-900">{new Date(stat.date).toLocaleDateString('de-DE')}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{stat.visitors}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Mobile-Ansicht: Karten statt Tabellen für sehr kleine Bildschirme */}
            <div className="block md:hidden mb-6">
              <h2 className="text-lg font-semibold mb-2">Besucherstatistik</h2>
              <div className="space-y-2">
                {visitorStats.slice(0, 5).map(stat => (
                  <div key={stat.date} className="bg-white rounded-lg shadow p-3 flex justify-between items-center">
                    <div className="text-sm text-gray-900">{new Date(stat.date).toLocaleDateString('de-DE')}</div>
                    <div className="text-sm font-medium text-gray-900">{stat.visitors} Besucher</div>
                  </div>
                ))}
              </div>
            </div>

            {/* NEU: Aktive Benutzer - Mobile optimiert */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Zuletzt aktive Benutzer</h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzer</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Letzte Aktivität</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zeit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activeUsers.map(user => (
                        <tr key={user.id}>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{user.name}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-900">{user.email}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-900">{formatLastActivity(user.last_activity)}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-500">{formatDateTime(user.last_activity)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Mobile-Ansicht: Karten statt Tabellen für sehr kleine Bildschirme */}
            <div className="block md:hidden mb-6">
              <h2 className="text-lg font-semibold mb-2">Zuletzt aktive Benutzer</h2>
              <div className="space-y-2">
                {activeUsers.slice(0, 5).map(user => (
                  <div key={user.id} className="bg-white rounded-lg shadow p-3">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{user.email}</div>
                    <div className="flex justify-between items-center mt-2">
                      <div className="text-xs text-gray-900">{formatLastActivity(user.last_activity)}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(user.last_activity)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Neueste Produkte - Mobile optimiert */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-semibold">Neueste Produkte</h2>
                <button 
                  onClick={() => setActiveTab('products')}
                  className="text-orange-500 hover:underline text-xs sm:text-sm"
                >
                  Alle anzeigen
                </button>
              </div>
              
              {/* Desktop-Tabelle */}
              <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produkt</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verkäufer</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentProducts.slice(0, 5).map(product => (
                        <tr key={product.id}>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                                <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover" src={product.image_url || "/placeholder.jpg"} alt="" />
                              </div>
                              <div className="ml-3 sm:ml-4">
                                <div className="text-xs sm:text-sm font-medium text-gray-900">{product.title}</div>
                                <div className="text-xs sm:text-sm text-gray-500">{product.category}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-900">{product.seller_name || 'Unbekannt'}</div>
                            <div className="text-xs sm:text-sm text-gray-500">{product.seller_club || 'Kein Verein'}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{formatPrice(product.price)} €</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            <div className="text-xs sm:text-sm text-gray-900">{new Date(product.created_at).toLocaleDateString()}</div>
                          </td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                            {product.is_archived ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Archiviert
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Aktiv
                              </span>
                            )}
                          </td>
                        </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Mobile-Karten */}
                  <div className="block md:hidden space-y-2">
                    {recentProducts.slice(0, 5).map(product => (
                      <div key={product.id} className="bg-white rounded-lg shadow border p-3">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <img className="h-10 w-10 rounded-full object-cover" src={product.image_url || "/placeholder.jpg"} alt="" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{product.title}</div>
                            <div className="text-xs text-gray-500">{product.category}</div>
                          </div>
                        </div>
                        <div className="mt-2 flex justify-between text-xs">
                          <div>
                            <div className="text-gray-500">Verkäufer</div>
                            <div className="font-medium">{product.seller_name || 'Unbekannt'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Preis</div>
                            <div className="font-medium">{formatPrice(product.price)} €</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Status</div>
                            <div>
                              {product.is_archived ? (
                                <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  Archiv
                                </span>
                              ) : (
                                <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                                  Aktiv
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
    
                {/* Neueste Benutzer - Mobile optimiert */}
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                  <div className="flex justify-between items-center mb-3 sm:mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Neueste Benutzer</h2>
                    <button 
                      onClick={() => setActiveTab('users')}
                      className="text-orange-500 hover:underline text-xs sm:text-sm"
                    >
                      Alle anzeigen
                    </button>
                  </div>
    
                  {/* Desktop-Tabelle */}
                  <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzer</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verein</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registriert am</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {recentUsers.slice(0, 5).map(user => (
                            <tr key={user.id}>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">{user.name}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                                <div className="text-xs sm:text-sm text-gray-900">{user.email}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                                <div className="text-xs sm:text-sm text-gray-900">{user.club || 'Kein Verein'}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                                <div className="text-xs sm:text-sm text-gray-900">{new Date(user.created_at).toLocaleDateString()}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                                {user.status === 'banned' ? (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    Gesperrt
                                  </span>
                                ) : (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Aktiv
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Mobile-Karten */}
                  <div className="block md:hidden space-y-2">
                    {recentUsers.slice(0, 5).map(user => (
                      <div key={user.id} className="bg-white rounded-lg shadow border p-3">
                        <div className="flex justify-between">
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div>
                            {user.status === 'banned' ? (
                              <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-red-100 text-red-800">
                                Gesperrt
                              </span>
                            ) : (
                              <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                                Aktiv
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">{user.email}</div>
                        <div className="mt-2 flex justify-between text-xs">
                          <div>
                            <span className="text-gray-500">Verein:</span> {user.club || 'Kein Verein'}
                          </div>
                          <div>
                            <span className="text-gray-500">Registriert:</span> {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
    
            {/* Products Tab - Mobile optimiert */}
            {activeTab === 'products' && (
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
                  <h2 className="text-lg sm:text-xl font-semibold">Alle Produkte verwalten</h2>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                    <input 
                      type="text" 
                      placeholder="Produkt suchen..." 
                      className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto"
                    />
                    <select className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto">
                      <option value="all">Alle</option>
                      <option value="active">Aktiv</option>
                      <option value="archived">Archiviert</option>
                    </select>
                  </div>
                </div>
    
                {/* Desktop-Tabelle */}
                <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produkt</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verkäufer</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentProducts.map(product => (
                          <tr key={product.id}>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">#{product.id}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                                  <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover" src={product.image_url || "/placeholder.jpg"} alt="" />
                                </div>
                                <div className="ml-3 sm:ml-4">
                                  <div className="text-xs sm:text-sm font-medium text-gray-900">{product.title}</div>
                                  <div className="text-xs sm:text-sm text-gray-500">{product.category}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{product.seller_name || 'Unbekannt'}</div>
                              <div className="text-xs sm:text-sm text-gray-500">{product.seller_club || 'Kein Verein'}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">{formatPrice(product.price)} €</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{new Date(product.created_at).toLocaleDateString()}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              {product.is_archived ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  Archiviert
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Aktiv
                                </span>
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <Link 
                                  to={`/products/${product.id}`}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Ansehen
                                </Link>
                                <button 
                                  className="text-red-600 hover:text-red-900"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  Löschen
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Mobile-Karten */}
                <div className="block md:hidden space-y-3">
                  {recentProducts.map(product => (
                    <div key={product.id} className="bg-white rounded-lg shadow border p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <img className="h-10 w-10 rounded-full object-cover" src={product.image_url || "/placeholder.jpg"} alt="" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{product.title}</div>
                            <div className="text-xs text-gray-500">{product.category}</div>
                          </div>
                        </div>
                        <div>
                          {product.is_archived ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Archiv
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                              Aktiv
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Verkäufer:</span> {product.seller_name || 'Unbekannt'}
                        </div>
                        <div>
                          <span className="text-gray-500">Preis:</span> <span className="font-medium">{formatPrice(product.price)} €</span>
                        </div>
                        <div>
                          <span className="text-gray-500">ID:</span> #{product.id}
                        </div>
                        <div>
                          <span className="text-gray-500">Datum:</span> {new Date(product.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between">
                        <Link 
                          to={`/products/${product.id}`}
                          className="text-blue-600 hover:text-blue-900 text-sm"
                        >
                          Ansehen
                        </Link>
                        <button 
                          className="text-red-600 hover:text-red-900 text-sm"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
    
            {/* Users Tab - Mobile optimiert */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
                  <h2 className="text-lg sm:text-xl font-semibold">Benutzer verwalten</h2>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                    <input 
                      type="text" 
                      placeholder="Benutzer suchen..." 
                      className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto"
                    />
                    <select className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto">
                      <option value="all">Alle</option>
                      <option value="active">Aktiv</option>
                      <option value="banned">Gesperrt</option>
                    </select>
                  </div>
                </div>
    
                {/* Desktop-Tabelle */}
                <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verein</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registriert</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentUsers.map(user => (
                          <tr key={user.id}>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">#{user.id}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">{user.name}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{user.email}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{user.club || 'Kein Verein'}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">{new Date(user.created_at).toLocaleDateString()}</div>
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                              {user.status === 'banned' ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                  Gesperrt
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Aktiv
                                </span>
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button 
                                  className="text-blue-600 hover:text-blue-900"
                                  onClick={() => alert(`Details für Benutzer ${user.id}`)}
                                >
                                  Details
                                </button>
                                {user.status !== 'banned' ? (
                                  <button 
                                    className="text-red-600 hover:text-red-900"
                                    onClick={() => handleBanUser(user.id)}
                                  >
                                    Sperren
                                  </button>
                                ) : (
                                  <button 
                                    className="text-green-600 hover:text-green-900"
                                    onClick={() => alert('Entsperren-Funktion noch nicht implementiert')}
                                  >
                                    Entsperren
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Mobile-Karten */}
                <div className="block md:hidden space-y-3">
                  {recentUsers.map(user => (
                    <div key={user.id} className="bg-white rounded-lg shadow border p-3">
                      <div className="flex justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{user.email}</div>
                        </div>
                        <div>
                          {user.status === 'banned' ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-red-100 text-red-800">
                              Gesperrt
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                              Aktiv
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">ID:</span> #{user.id}
                        </div>
                        <div>
                          <span className="text-gray-500">Verein:</span> {user.club || 'Kein Verein'}
                        </div>
                        <div>
                          <span className="text-gray-500">Registriert:</span> {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between">
                        <button 
                          className="text-blue-600 hover:text-blue-900 text-sm"
                          onClick={() => alert(`Details für Benutzer ${user.id}`)}
                        >
                          Details
                        </button>
                        {user.status !== 'banned' ? (
                          <button 
                            className="text-red-600 hover:text-red-900 text-sm"
                            onClick={() => handleBanUser(user.id)}
                          >
                            Sperren
                          </button>
                        ) : (
                          <button 
                            className="text-green-600 hover:text-green-900 text-sm"
                            onClick={() => alert('Entsperren-Funktion noch nicht implementiert')}
                          >
                            Entsperren
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
    
            {/* Settings Tab - Mobile optimiert */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Admin-Einstellungen</h2>
                
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4">Website-Einstellungen</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Website-Name</label>
                      <input 
                        type="text" 
                        className="border rounded w-full px-3 py-2 text-sm" 
                        defaultValue="SportyReplay"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Kontakt-E-Mail</label>
                      <input 
                        type="email" 
                        className="border rounded w-full px-3 py-2 text-sm" 
                        defaultValue="info@sportyreplay.de"
                      />
                    </div>
                  </div>
                </div>
               
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4">Sicherheitseinstellungen</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="enable_2fa" 
                        className="h-4 w-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500" 
                        defaultChecked={false}
                      />
                      <label htmlFor="enable_2fa" className="ml-2 block text-xs sm:text-sm text-gray-900">
                        Zwei-Faktor-Authentifizierung aktivieren
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="require_email_verification" 
                        className="h-4 w-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500" 
                        defaultChecked={true}
                      />
                      <label htmlFor="require_email_verification" className="ml-2 block text-xs sm:text-sm text-gray-900">
                        E-Mail-Verifizierung für neue Benutzer erforderlich
                      </label>
                    </div>
                  </div>
                </div>
            
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4">Benachrichtigungseinstellungen</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="notify_new_user" 
                        className="h-4 w-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500" 
                        defaultChecked={true}
                      />
                      <label htmlFor="notify_new_user" className="ml-2 block text-xs sm:text-sm text-gray-900">
                        Benachrichtigung bei neuen Benutzerregistrierungen
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="notify_new_product" 
                        className="h-4 w-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500" 
                        defaultChecked={true}
                      />
                      <label htmlFor="notify_new_product" className="ml-2 block text-xs sm:text-sm text-gray-900">
                        Benachrichtigung bei neuen Produkteinstellungen
                      </label>
                    </div>
                  </div>
                </div>
            
                <div className="flex justify-end">
                  <button className="bg-orange-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded text-sm sm:text-base hover:bg-orange-600">
                    Einstellungen speichern
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };
    
    export default AdminDashboard;